export type PlayerTendencyField = {
	key: string;
	label: string;
	description: string;
};

export const getTendencyOverrideMultiplier = (value: number | undefined) => {
	if (value === undefined || value === 0) {
		return 1;
	}
	if (value <= -100) {
		return 0;
	}
	if (value < 0) {
		return Math.max(0, 1 + value / 100);
	}
	return value;
};

export const SHARED_PLAYER_TENDENCY_FIELDS: PlayerTendencyField[] = [
	{
		key: "ironManFactor",
		label: "Iron Man Factor",
		description:
			"Artificially boosts or reduces endurance and fatigue resistance without changing base ratings or OVR.",
	},
];

export const BASKETBALL_PLAYER_TENDENCY_FIELDS: PlayerTendencyField[] = [
	{
		key: "threePointAccuracy",
		label: "3-Point Accuracy",
		description:
			"Adjust 3-point shooting accuracy. Positive values increase accuracy, negative values decrease it.",
	},
	{
		key: "threePointTendency",
		label: "3-Point Tendency",
		description:
			"Adjust likelihood to attempt 3-pointers. Positive values make the player more likely to shoot 3s.",
	},
	{
		key: "twoPointAccuracy",
		label: "2-Point Accuracy",
		description:
			"Adjust 2-point shooting accuracy. Positive values increase accuracy, negative values decrease it.",
	},
	{
		key: "twoPointTendency",
		label: "2-Point Tendency",
		description:
			"Adjust likelihood to attempt 2-pointers. Positive values make the player more likely to shoot 2s.",
	},
	{
		key: "shootingFrequency",
		label: "Shooting Frequency",
		description:
			"Controls how often a player attempts shots when they have the ball. Higher values mean more aggressive shooting, lower values mean more passing.",
	},
	{
		key: "turnoverTendency",
		label: "Turnover Tendency",
		description:
			"Adjust likelihood to commit turnovers. Positive values increase turnovers, negative values reduce them.",
	},
	{
		key: "offRebTendency",
		label: "Offensive Rebound Tendency",
		description:
			"Adjust likelihood to pursue offensive rebounds. Positive values increase offensive rebounding effort.",
	},
	{
		key: "defRebTendency",
		label: "Defensive Rebound Tendency",
		description:
			"Adjust likelihood to pursue defensive rebounds. Positive values increase defensive rebounding effort.",
	},
	{
		key: "blockTendency",
		label: "Block Tendency",
		description:
			"Adjust likelihood to attempt blocks. Positive values increase block attempts, negative values reduce them.",
	},
	{
		key: "stealTendency",
		label: "Steal Tendency",
		description:
			"Adjust likelihood to attempt steals. Positive values increase steal attempts, negative values reduce them.",
	},
	{
		key: "ftAccuracy",
		label: "Free Throw Accuracy",
		description:
			"Adjust free throw shooting accuracy. Positive values increase accuracy, negative values decrease it.",
	},
	{
		key: "assistFactor",
		label: "Assist Factor",
		description:
			"Adjust likelihood to make assists. Positive values increase assists, negative values reduce them.",
	},
	{
		key: "defensiveFactor",
		label: "Defensive Factor",
		description:
			"Adjust defensive effectiveness in contesting opponent shots. Positive values enhance defense, negative values reduce it.",
	},
	{
		key: "fouledRate",
		label: "Fouled Rate",
		description:
			"Adjust likelihood to draw fouls when shooting. Positive values increase foul drawing, negative values reduce it.",
	},
	{
		key: "foulTendency",
		label: "Personal Foul Tendency",
		description:
			"Adjust likelihood to commit personal/shooting fouls. Positive values increase fouling rate, negative values reduce it.",
	},
	{
		key: "ballDominationTendency",
		label: "Ball Domination (Isolation)",
		description:
			"Adjust ball domination/isolation versus off-ball play. Positive values make the player play more isolation, negative values make them more of an off-ball player.",
	},
	{
		key: "intangibleImpact",
		label: "Intangible Impact Factor (+/-)",
		description:
			"Adjusts team-wide performance when this player is on court versus on the bench without changing the team's overall win rate.",
	},
];

export const FOOTBALL_SHARED_PLAYER_TENDENCY_FIELDS: PlayerTendencyField[] = [
	{
		key: "fumbleTendency",
		label: "Fumble Tendency",
		description:
			"Adjust this player's fumble risk. Higher values make fumbles more likely; negative values make the player more secure with the ball.",
	},
	{
		key: "returnYardsTendency",
		label: "Return Yards Tendency",
		description:
			"For players used as kickoff or punt returners, adjusts the average return yardage using their speed and rushing composites.",
	},
	{
		key: "returnBreakawayTendency",
		label: "Return Breakaway Tendency",
		description:
			"For returners, adjusts the chance of an explosive return that can create a return touchdown.",
	},
	{
		key: "returnFumbleTendency",
		label: "Return Fumble Tendency",
		description:
			"For returners, adjusts the chance of fumbling during a kickoff or punt return.",
	},
	...SHARED_PLAYER_TENDENCY_FIELDS,
];

const QB_TENDENCY_FIELDS: PlayerTendencyField[] = [
	{
		key: "passTendency",
		label: "Pass Tendency",
		description:
			"Adjusts how pass-heavy this quarterback's offense is. It changes pass/run opportunity while preserving the normal situational limits.",
	},
	{
		key: "passAccuracy",
		label: "Pass Accuracy",
		description:
			"Adjusts the quarterback's passing-accuracy composite, affecting completions and interception risk without changing the displayed rating.",
	},
	{
		key: "deepPassTendency",
		label: "Deep/Long Pass Tendency",
		description:
			"Adjusts how often this quarterback attempts a true deep shot. Deep shots use a longer yardage distribution and carry more completion risk; they are not guaranteed to gain a specific number of yards.",
	},
	{
		key: "deepPassAccuracy",
		label: "Deep Pass Accuracy",
		description:
			"Adjusts completion accuracy on deep shots only. It is separate from the quarterback's general pass accuracy.",
	},
	{
		key: "interceptionTendency",
		label: "Interception Tendency",
		description:
			"Adjusts risky-throw behavior independently of passing ability. Positive values increase interceptions; negative values reduce them.",
	},
	{
		key: "sackTendency",
		label: "Sack Tendency",
		description:
			"Adjusts how often this quarterback takes a sack after accounting for pass rush, blocking, and sack-avoidance ability.",
	},
	{
		key: "scrambleTendency",
		label: "Scramble Tendency",
		description:
			"Adjusts how willing this quarterback is to scramble instead of staying in the pocket.",
	},
	{
		key: "rushTendency",
		label: "QB Rush Tendency",
		description:
			"Adjusts the quarterback's share of designed runs when the play's runner pool includes the QB.",
	},
	{
		key: "rushEfficiency",
		label: "QB Rush Efficiency",
		description:
			"Adjusts rushing-yardage efficiency for this quarterback's scrambles and designed runs.",
	},
];

const SKILL_PLAYER_TENDENCY_FIELDS: PlayerTendencyField[] = [
	{
		key: "targetTendency",
		label: "Target Tendency",
		description:
			"Adjusts how often this player is selected as a pass target relative to other eligible receivers.",
	},
	{
		key: "catchAccuracy",
		label: "Catch Accuracy",
		description:
			"Adjusts this player's catching composite when converting targets into receptions.",
	},
	{
		key: "routeDepthTendency",
		label: "Route Depth Tendency",
		description:
			"Adjusts the average depth of this player's routes. Higher values create longer targets and lower catch rates may result from greater difficulty.",
	},
];

const BLOCKING_TENDENCY_FIELDS: PlayerTendencyField[] = [
	{
		key: "passBlockTendency",
		label: "Pass-Block Tendency",
		description:
			"Adjusts how often this RB or TE stays in to help protect the quarterback instead of releasing into a route.",
	},
	{
		key: "passBlockAccuracy",
		label: "Pass-Block Accuracy",
		description:
			"Adjusts this player's chance of winning an assigned pass block and feeds the play's effective pass protection.",
	},
	{
		key: "runBlockTendency",
		label: "Run-Block Tendency",
		description:
			"Adjusts how often this RB or TE stays engaged as a run blocker when not carrying the ball.",
	},
	{
		key: "runBlockAccuracy",
		label: "Run-Block Accuracy",
		description:
			"Adjusts this player's chance of winning an assigned run block and feeds the play's effective run blocking.",
	},
];

const SPECIAL_TEAMS_TENDENCY_FIELDS: Record<string, PlayerTendencyField[]> = {
	K: [
		{
			key: "fieldGoalAccuracy",
			label: "Field-Goal Accuracy",
			description:
				"Adjusts this kicker's field-goal probability after distance, kicking power, and normal kicking accuracy are considered.",
		},
		{
			key: "fieldGoalPower",
			label: "Field-Goal Power",
			description:
				"Adjusts the effective leg strength used when calculating field-goal distance.",
		},
		{
			key: "kickoffDistanceTendency",
			label: "Kickoff Distance Tendency",
			description:
				"Adjusts ordinary kickoff depth. It can increase or reduce touchback and return opportunities without changing the kicker's displayed power rating.",
		},
	],
	P: [
		{
			key: "puntDistanceTendency",
			label: "Punt Distance Tendency",
			description:
				"Adjusts ordinary punt distance around the punter's normal power and the league baseline.",
		},
		{
			key: "puntPlacementTendency",
			label: "Punt Placement Tendency",
			description:
				"Adjusts the chance of pinning a punt inside the 20 instead of sending it into a touchback or a normal return.",
		},
	],
};

export const getFootballPlayerTendencyFields = (
	pos: string,
): PlayerTendencyField[] => {
	const shared = FOOTBALL_SHARED_PLAYER_TENDENCY_FIELDS;
	if (pos === "QB") {
		return [...shared, ...QB_TENDENCY_FIELDS];
	}
	if (pos === "RB") {
		return [
			...shared,
			...SKILL_PLAYER_TENDENCY_FIELDS,
			...BLOCKING_TENDENCY_FIELDS,
			{
				key: "rushTendency",
				label: "Rush Tendency",
				description:
					"Adjusts how often this player is selected for carries when available in the runner pool.",
			},
			{
				key: "rushEfficiency",
				label: "Rush Efficiency",
				description:
					"Adjusts this player's rushing-yardage efficiency without changing the displayed rushing rating.",
			},
		];
	}
	if (pos === "WR") {
		return [...shared, ...SKILL_PLAYER_TENDENCY_FIELDS];
	}
	if (pos === "TE") {
		return [
			...shared,
			...SKILL_PLAYER_TENDENCY_FIELDS,
			...BLOCKING_TENDENCY_FIELDS,
		];
	}
	return [...shared, ...(SPECIAL_TEAMS_TENDENCY_FIELDS[pos] ?? [])];
};
